export type AppErrorCode =
  | "invalid-pdf"
  | "pdf-too-large"
  | "pdf-password-required"
  | "pdf-password-incorrect"
  | "pdf-corrupt"
  | "pdf-render-failed"
  | "storage-open-failed"
  | "storage-quota-exceeded"
  | "storage-write-failed"
  | "import-invalid"
  | "import-unsupported-version"
  | "annotation-conflict"
  | "typst-initialization-failed"
  | "typst-render-failed";

export class AppError extends Error {
  readonly code: AppErrorCode;

  constructor(
    code: AppErrorCode,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = "AppError";
    this.code = code;
  }
}

export class PdfFileValidationError extends AppError {
  readonly byteLength: number;
  readonly maximumByteLength: number | null;

  constructor(
    code: "invalid-pdf" | "pdf-too-large",
    message: string,
    details: {
      byteLength: number;
      maximumByteLength?: number;
      cause?: unknown;
    },
  ) {
    super(code, message, { cause: details.cause });
    this.name = "PdfFileValidationError";
    this.byteLength = details.byteLength;
    this.maximumByteLength = details.maximumByteLength ?? null;
  }
}

export class RevisionConflictError extends AppError {
  readonly annotationId: string;
  readonly expectedRevision: number;
  readonly actualRevision: number;

  constructor(
    annotationId: string,
    expectedRevision: number,
    actualRevision: number,
  ) {
    super(
      "annotation-conflict",
      `Annotation ${annotationId} changed from revision ${expectedRevision} to ${actualRevision}.`,
    );
    this.name = "RevisionConflictError";
    this.annotationId = annotationId;
    this.expectedRevision = expectedRevision;
    this.actualRevision = actualRevision;
  }
}

export class StorageError extends AppError {
  readonly operation: string;

  constructor(
    code:
      | "storage-open-failed"
      | "storage-quota-exceeded"
      | "storage-write-failed",
    message: string,
    operation: string,
    options?: ErrorOptions,
  ) {
    super(code, message, options);
    this.name = "StorageError";
    this.operation = operation;
  }
}

export class ImportValidationError extends AppError {
  readonly issues: readonly string[];

  constructor(
    message: string,
    options?: {
      issues?: readonly string[];
      cause?: unknown;
    },
  ) {
    super("import-invalid", message, { cause: options?.cause });
    this.name = "ImportValidationError";
    this.issues = options?.issues ?? [];
  }
}

export class UnsupportedImportVersionError extends AppError {
  readonly format: string | null;
  readonly formatVersion: number | null;

  constructor(format: string | null, formatVersion: number | null) {
    super(
      "import-unsupported-version",
      `Unsupported Marginalia import format${
        format === null ? "" : ` "${format}"`
      }${formatVersion === null ? "" : ` version ${formatVersion}`}.`,
    );
    this.name = "UnsupportedImportVersionError";
    this.format = format;
    this.formatVersion = formatVersion;
  }
}

export class MissingRecordError extends Error {
  readonly recordType: string;
  readonly recordId: string;

  constructor(recordType: string, recordId: string) {
    super(`${recordType} ${recordId} was not found.`);
    this.name = "MissingRecordError";
    this.recordType = recordType;
    this.recordId = recordId;
  }
}
